/**
 * Rows of "My office network" (CU-869fmjzrh CA 16, 19): group by country, office type or nothing, a summary row per
 * group (the median of its offices' distances per column, and how many are above the market), and the sort — by name,
 * by the distance in a column or by its change, inside each group. Clicking a column header sorts by it, most above
 * first; a second click on the same header reverses it. Pure and alias-free.
 */
import { median } from '../../../@services/stats.js'
import { NEUTRAL } from './colourScale.js'

export const GROUP_BY = Object.freeze(['country', 'type', 'none'])
export const TYPE_LABEL = Object.freeze({ airport: 'Airport', downtown: 'City', train_station: 'Train station', truck: 'Trucks', other: 'Other' })
export const COLLAPSE_OVER = 40

const isNum = v => typeof v === 'number' && Number.isFinite(v)
const byName = (a, b) => String(a.office.name).localeCompare(String(b.office.name)) || a.office.id - b.office.id

/** What a cell gives a summary or a sort: its distance, or the market change without identity */
export const valueOf = (cell, mode) => (mode === 'market' ? cell?.marketChange : cell?.distance)
const changeOf = (cell, mode) => (mode === 'market' ? cell?.marketChange : cell?.change)

/** `{ key, label, rows }` in label order; `none` → one group without a header */
export function groupRows(rows, by = 'country') {
	if (by === 'none') return [{ key: 'all', label: null, rows: [...rows] }]
	const groups = new Map()
	for (const row of rows) {
		const key = by === 'type' ? row.office.type || 'other' : row.office.country || '—'
		const label = by === 'type' ? TYPE_LABEL[key] || key : row.office.countryName || row.office.country || 'No country'
		if (!groups.has(key)) groups.set(key, { key, label, rows: [] })
		groups.get(key).rows.push(row)
	}
	return [...groups.values()].sort((a, b) => String(a.label).localeCompare(String(b.label)))
}

/** Per column: `{ median, above, withData }` over the group's offices with a value */
export function groupSummary(rows, nColumns, mode = 'brand') {
	return Array.from({ length: nColumns }, (_, j) => {
		const values = rows.map(r => valueOf(r.cells[j], mode)).filter(isNum)
		return { median: values.length ? median(values) : null, above: values.filter(v => v >= NEUTRAL).length, withData: values.length }
	})
}

/**
 * `sort` = `{ key: 'name' | 'col:<j>' | 'change:<j>', dir: 'desc' | 'asc' }`. desc on a column = most above the market
 * first. Rows without a value go last, by name, whatever the direction.
 */
export function sortRows(rows, sort = { key: 'name', dir: 'asc' }, mode = 'brand') {
	const s = sort || { key: 'name', dir: 'asc' }
	if (!s.key || s.key === 'name') return [...rows].sort((a, b) => (s.dir === 'desc' ? -byName(a, b) : byName(a, b)))
	const [kind, j] = s.key.split(':')
	const get = r => (kind === 'change' ? changeOf(r.cells[+j], mode) : valueOf(r.cells[+j], mode))
	const sign = s.dir === 'asc' ? 1 : -1
	return [...rows].sort((a, b) => {
		const va = get(a), vb = get(b)
		if (!isNum(va) && !isNum(vb)) return byName(a, b)
		if (!isNum(va)) return 1
		if (!isNum(vb)) return -1
		return sign * (va - vb) || byName(a, b)
	})
}

/** A click on a header: the same column flips the direction; another one starts on most above (desc) */
export function nextSort(current, clickedKey) {
	if (current?.key === clickedKey) return { key: clickedKey, dir: current.dir === 'desc' ? 'asc' : 'desc' }
	return { key: clickedKey, dir: clickedKey === 'name' ? 'asc' : 'desc' }
}

/** The groups as shown: each group's rows sorted, with its summary; more than 40 offices → collapsed by default */
export function groupedView(model, { by = 'country', sort = null, collapsed = null } = {}) {
	const groups = groupRows(model.rows, by).map(g => ({
		...g,
		rows: sortRows(g.rows, sort, model.mode),
		summary: groupSummary(g.rows, model.columns?.length || 0, model.mode),
		notCaptured: g.rows.filter(r => r.reason === 'notCaptured').length,
	}))
	const defaultCollapsed = by !== 'none' && model.rows.length > COLLAPSE_OVER
	return groups.map(g => ({ ...g, collapsed: collapsed && g.key in collapsed ? !!collapsed[g.key] : defaultCollapsed }))
}

/** "Find an office": only finds a row, never changes a figure */
export const findRows = (rows, q) => {
	const query = String(q || '').trim().toLowerCase()
	return query ? rows.filter(r => String(r.office.name).toLowerCase().includes(query)) : rows
}
